import { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import SignIn from './SignIn';
import myTheme from './MyTheme';
import { ThemeProvider } from '@mui/material/styles';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

/**
 * Routes are code-split so a screen's JavaScript only downloads when it is first
 * opened. This keeps the 3D engine (NiiVue, pulled in by the viewer routes) out
 * of the worklist, which previously paid for it on every page load. SignIn stays
 * eager: it is the entry point, so splitting it would only add a round trip.
 */
const Dashboard     = lazy(() => import('./Dashboard'));
const SignUp        = lazy(() => import('./SignUp'));
const Upload        = lazy(() => import('./Upload'));
const UploadZip     = lazy(() => import('./UploadZip'));
const View          = lazy(() => import('./View'));
const Workstation   = lazy(() => import('./Workstation'));
const Report        = lazy(() => import('./Report'));
const ReportHistory = lazy(() => import('./ReportHistory'));
const PACSConnect   = lazy(() => import('./PACSConnect'));
const ManageClients = lazy(() => import('./ManageClients'));
const Analytics     = lazy(() => import('./Analytics'));
const Shared        = lazy(() => import('./Shared'));
const Users         = lazy(() => import('./Users'));
const Orgs          = lazy(() => import('./Orgs'));
const Logs          = lazy(() => import('./Logs'));
const AIModels      = lazy(() => import('./AIModels'));
const MyProfile     = lazy(() => import('./MyProfile'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
             height: '100vh', background: '#0a0f15' }}>
    <CircularProgress sx={{ color: '#14b8a6' }} size={52} />
  </Box>
);

const App = () => (
  <ThemeProvider theme={myTheme}>
    <div className="app" style={{ width: '100%', minHeight: '100vh' }}>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/signin"          element={<SignIn />} />
            <Route path="/signup"          element={<SignUp />} />
            <Route path="/dashboard"       element={<Dashboard />} />
            <Route path="/upload"          element={<Upload />} />
            <Route path="/uploadzip"       element={<UploadZip />} />
            <Route path="/viewer/:job_id"  element={<View />} />
            <Route path="/workstation/:job_id" element={<Workstation />} />
            <Route path="/report/:job_id"  element={<Report />} />
            <Route path="/report-history/:job_id" element={<ReportHistory />} />
            <Route path="/pacs-connect"   element={<PACSConnect />} />
            <Route path="/clients"       element={<ManageClients />} />
            <Route path="/analytics"     element={<Analytics />} />
            <Route path="/users"         element={<Users />} />
            <Route path="/orgs"          element={<Orgs />} />
            <Route path="/logs"          element={<Logs />} />
            <Route path="/ai-models"     element={<AIModels />} />
            <Route path="/my-profile"     element={<MyProfile />} />
            <Route path="/shared/:token"  element={<Shared />} />
            <Route path="/"                element={<Navigate to="/dashboard" />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  </ThemeProvider>
);

export default App;
